import { Link } from "react-router-dom";
import useSWR from "swr";
import { Clock, IdCard, MapPin, Truck, Search, ShoppingBag, ShieldCheck, Tags } from "lucide-react";
import { Seo } from "@/components/Seo";
import { CategoryTiles, LocationChips } from "@/components/Tiles";
import { GridSkeleton, ProductGrid } from "@/components/ProductCard";
import { CtaBand, SectionHead } from "@/components/CtaBand";
import { FaqList } from "@/components/FaqList";
import { BlogCard } from "@/pages/Blog";
import { ReviewBanner } from "@/components/ReviewBanner";
import { useSite } from "@/context/Providers";
import { fetcher, swrOpts } from "@/lib/api";
import { businessLd, faqLd, websiteLd } from "@/lib/seo";
import { GENERAL_FAQS } from "@/content/pages";

const HERO_IMG = "https://images.pexels.com/photos/36658177/pexels-photo-36658177.jpeg?auto=compress&cs=tinysrgb&w=1400";
const HOME_FAQS = GENERAL_FAQS.slice(0, 6);

const Hero = ({ site }) => (
  <section className="grain relative overflow-hidden border-b border-line" aria-labelledby="hero-title">
    <img src={HERO_IMG} alt="" width="1400" height="933" fetchPriority="high" className="absolute inset-0 h-full w-full object-cover opacity-20" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/40" aria-hidden />
    <div className="container-x relative grid items-center gap-10 py-16 sm:py-24 lg:grid-cols-[1.25fr_1fr] lg:py-28">
      <div className="animate-rise">
        <p className="eyebrow flex items-center gap-2"><MapPin className="h-4 w-4" aria-hidden />Georgia Ave NW · Washington, DC</p>
        <h1 id="hero-title" className="mt-5 text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
          Cannabis Dispensary &amp; Weed Delivery in <span className="text-gold">Washington, DC</span>
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-300 sm:text-lg">
          Shop flower, pre-rolls, edibles, vapes and concentrates online and get it delivered anywhere in the District. Free delivery on orders over ${site?.free_delivery_minimum ?? 50}.
        </p>
        <div className="mt-9 flex flex-col gap-3 sm:flex-row">
          <Link to="/shop/" className="btn-gold px-8" data-testid="hero-shop-now-button">Shop Now</Link>
          <Link to="/deals/" className="btn-ghost px-8" data-testid="hero-view-deals-button">View Deals</Link>
        </div>
        <ul className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-300 sm:flex sm:flex-wrap">
          <li className="flex items-center gap-2"><Truck className="h-4 w-4 text-jade" aria-hidden />Free delivery $50+</li>
          <li className="flex items-center gap-2"><IdCard className="h-4 w-4 text-jade" aria-hidden />21+ with valid ID</li>
          <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-jade" aria-hidden />Mon–Sat 8AM–8PM</li>
          <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-jade" aria-hidden />Delivering across DC</li>
        </ul>
      </div>
      <div className="relative mx-auto hidden w-full max-w-md lg:block" aria-hidden>
        <div className="absolute inset-6 rounded-full border border-gold/25" />
        <div className="absolute inset-0 rounded-full border border-line" />
        <div className="absolute inset-16 rounded-full bg-jade/10 blur-3xl" />
        <img src="/brand/shadc-logo-512.webp" alt="" width="512" height="512" className="logo-halo relative mx-auto w-[78%] py-12" />
      </div>
    </div>
  </section>
);

const BENEFITS = [
  { icon: Search, t: "Easy to browse", d: "Search by product, brand or strain and filter by price and availability." },
  { icon: Tags, t: "Clear pricing", d: "Every size and option is priced up front. Deals are marked right on the menu." },
  { icon: Truck, t: "DC-wide delivery", d: "From Park View to Navy Yard, we deliver to residential addresses across the District." },
  { icon: ShieldCheck, t: "Discreet & ID-verified", d: "Adults 21+ only. Every handoff is ID-checked for your safety and ours." },
];

const STEPS = [
  { icon: Search, t: "Browse", d: "Explore the menu by category, brand or strain." },
  { icon: ShoppingBag, t: "Add to cart", d: "Choose sizes or flavors — your cart saves as you go." },
  { icon: ShieldCheck, t: "Check out", d: "Confirm delivery details through secure checkout." },
  { icon: IdCard, t: "Receive", d: "Show a valid 21+ ID when your order arrives." },
];

const Products = ({ query, testid }) => {
  const { data } = useSWR(`/products?${query}`, fetcher, swrOpts);
  return <div data-testid={testid}>{data ? <ProductGrid items={data.items} /> : <GridSkeleton n={4} />}</div>;
};

export default function Home() {
  const site = useSite();
  const { data: blog } = useSWR("/blog?limit=3", fetcher, swrOpts);
  return (
    <>
      <Seo title="SHADC Dispensary | Cannabis Dispensary & Weed Delivery in Washington, DC"
        description="SHADC Dispensary on Georgia Ave NW offers cannabis delivery across Washington, DC — flower, pre-rolls, edibles, vapes and concentrates. Free delivery over $50. Adults 21+."
        path="/" jsonLd={[businessLd(site), websiteLd(), faqLd(HOME_FAQS)]} />
      <Hero site={site} />
      <ReviewBanner />

      <section className="border-b border-line bg-panel/50" aria-label="Why customers choose SHADC">
        <ul className="container-x grid gap-6 py-10 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map(({ icon: I, t, d }) => (
            <li key={t} className="flex gap-4"><I className="mt-1 h-6 w-6 shrink-0 text-gold" aria-hidden />
              <div><h3 className="font-sans text-base font-semibold text-white">{t}</h3><p className="mt-1 text-sm leading-relaxed text-gray-400">{d}</p></div></li>
          ))}
        </ul>
      </section>

      <section className="container-x py-16 sm:py-20" aria-labelledby="cats-title">
        <SectionHead eyebrow="The menu" title="Shop by Category" id="cats-title" action={<Link to="/shop/" className="link-gold hidden text-sm font-semibold sm:block">View all →</Link>} />
        <CategoryTiles />
      </section>

      <section className="container-x pb-16 sm:pb-20" aria-labelledby="featured-title">
        <SectionHead eyebrow="Customer favorites" title="Featured Products" id="featured-title" action={<Link to="/shop/" className="link-gold text-sm font-semibold">Shop all →</Link>} />
        <Products query="sort=featured&in_stock=true&limit=8" testid="featured-products" />
      </section>

      <section className="border-y border-line bg-panel/40 py-16 sm:py-20" aria-labelledby="how-title">
        <div className="container-x">
          <SectionHead eyebrow="Simple by design" title="How Ordering Works" id="how-title" />
          <ol className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(({ icon: I, t, d }, i) => (
              <li key={t} className="card-surface p-6">
                <span className="font-display text-4xl font-bold text-gold/40">0{i + 1}</span>
                <I className="mt-4 h-6 w-6 text-jade" aria-hidden />
                <h3 className="mt-3 font-sans text-lg font-semibold text-white">{t}</h3>
                <p className="mt-1 text-sm text-gray-400">{d}</p>
              </li>
            ))}
          </ol>
          <Link to="/how-to-order/" className="link-gold mt-8 inline-block text-sm font-semibold">Read the full ordering guide →</Link>
        </div>
      </section>

      <section className="container-x py-16 sm:py-20" aria-labelledby="deals-title">
        <SectionHead eyebrow="Limited time" title="Featured Deals" id="deals-title" action={<Link to="/deals/" className="link-gold text-sm font-semibold">All deals →</Link>} />
        <Products query="category=deals&sort=discount&limit=4" testid="featured-deals" />
      </section>

      <section className="border-y border-line bg-panel/40 py-16 sm:py-20" aria-labelledby="why-title">
        <div className="container-x grid gap-10 lg:grid-cols-2">
          <div>
            <p className="eyebrow">Why SHADC</p>
            <h2 id="why-title" className="mt-2 text-3xl font-semibold text-white sm:text-4xl">A Georgia Avenue dispensary, built for the whole District</h2>
            <p className="mt-5 text-base leading-relaxed text-gray-300">SHADC is based at 2906 Georgia Ave NW. We keep the menu organized, the pricing clear and the ordering process quick — so you can find what you want and get on with your day.</p>
            <div className="mt-8 flex flex-wrap gap-3"><Link to="/about/" className="btn-ghost">About us</Link><Link to="/washington-dc-dispensary/" className="btn-ghost">Our dispensary</Link></div>
          </div>
          <div>
            <h3 className="font-sans text-sm font-bold uppercase tracking-[0.18em] text-gray-400">Delivering to</h3>
            <div className="mt-4"><LocationChips /></div>
            <Link to="/locations/" className="link-gold mt-6 inline-block text-sm font-semibold" data-testid="home-service-areas-link">See all service areas →</Link>
          </div>
        </div>
      </section>

      <section className="container-x py-16 sm:py-20" aria-labelledby="faq-title">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.6fr]">
          <div>
            <p className="eyebrow">Good to know</p>
            <h2 id="faq-title" className="mt-2 text-3xl font-semibold text-white sm:text-4xl">Frequently Asked Questions</h2>
            <p className="mt-4 text-gray-400">Everything you need to know about ordering cannabis delivery in Washington, DC.</p>
            <Link to="/faq/" className="link-gold mt-6 inline-block text-sm font-semibold">View all FAQs →</Link>
          </div>
          <FaqList faqs={HOME_FAQS} id="home-faq" />
        </div>
      </section>

      {blog?.items?.length > 0 && (
        <section className="container-x pb-4" aria-labelledby="guides-title">
          <SectionHead eyebrow="Learn" title="Guides & Articles" id="guides-title" action={<Link to="/blog/" className="link-gold text-sm font-semibold">All articles →</Link>} />
          <div className="grid gap-5 md:grid-cols-3">{blog.items.map((p) => <BlogCard key={p.slug} p={p} />)}</div>
        </section>
      )}

      <CtaBand title="Your next order is a few taps away" />
    </>
  );
}
